import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLanguage } from "../i18n/LanguageContext";

const HERO_IMG = "https://images.unsplash.com/photo-1598737285721-29346a5c9278?q=80&w=2400&auto=format&fit=crop";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "45%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} className="relative h-[100svh] min-h-[640px] overflow-hidden" data-testid="hero-section">
      <motion.div className="absolute inset-0" style={{ y: bgY }}>
        <motion.img
          src={HERO_IMG}
          alt="Luxury mega yacht cruising the Mediterranean"
          className="w-full h-full object-cover"
          initial={{ scale: 1.15 }}
          animate={{ scale: 1 }}
          transition={{ duration: 2.4, ease: [0.16, 1, 0.3, 1] }}
          data-testid="hero-image"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-[#050B14]/70 via-[#050B14]/35 to-[#050B14]" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#050B14]/60 via-transparent to-transparent" />

      <motion.div
        style={{ y: contentY, opacity: fade }}
        className="relative z-10 h-full max-w-7xl mx-auto px-6 lg:px-10 flex flex-col justify-end pb-28 sm:pb-32"
      >
        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="font-label text-[10px] sm:text-xs tracking-[0.45em] uppercase text-[#D4AF37] mb-6"
          data-testid="hero-overline"
        >
          {t.hero.overline}
        </motion.p>

        <h1 className="font-display font-light text-[#F7F5F0] text-4xl sm:text-5xl lg:text-6xl leading-[1.08] tracking-tight" data-testid="hero-title">
          <MaskedLine delay={0.65}>{t.hero.line1}</MaskedLine>
          <MaskedLine delay={0.8}>
            <em className="text-[#D4AF37] not-italic sm:italic">{t.hero.line2}</em>
          </MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.15, ease: [0.16, 1, 0.3, 1] }}
          className="mt-7 max-w-xl text-sm sm:text-base text-slate-300 font-light leading-relaxed"
          data-testid="hero-subtitle"
        >
          {t.hero.sub}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.3, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            onClick={() => document.getElementById("fleet")?.scrollIntoView({ behavior: "smooth" })}
            data-testid="hero-explore-fleet-button"
            className="font-label text-[11px] tracking-[0.25em] uppercase px-8 py-4 bg-[#D4AF37] text-[#050B14] font-semibold hover:bg-[#F3E5AB] transition-colors duration-300"
          >
            {t.hero.ctaPrimary}
          </button>
          <button
            onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}
            data-testid="hero-plan-charter-button"
            className="font-label text-[11px] tracking-[0.25em] uppercase px-8 py-4 border border-[rgba(247,245,240,0.35)] text-[#F7F5F0] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors duration-300"
          >
            {t.hero.ctaSecondary}
          </button>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.9, duration: 1 }}
        className="absolute bottom-8 right-6 lg:right-10 z-10 flex items-center gap-3"
        data-testid="hero-scroll-indicator"
      >
        <span className="font-label text-[10px] tracking-[0.4em] uppercase text-slate-400">{t.hero.scroll}</span>
        <div className="w-px h-14 bg-gradient-to-b from-[#D4AF37] to-transparent" />
      </motion.div>
    </section>
  );
};
